/**
 * Clutch Reel sheet: shows the plays Clutch picked from the fan's recording, composes the
 * vertical reel (solo, or side-by-side with a friend's take of the same plays), then
 * preview → share / download.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { Fan, GameEvent, RoomSnapshot, Take } from '@clutch/core';
import { Headshot } from '../ui/Media';
import { choiceLabel, firstName } from '../room/derive';
import type { LockUi } from '../room/useRoom';
import { callLine } from './layout';
import { pairSegments, reelCaption, selectReelSegments } from './segments';
import { composeReel, shareReel, type ReelResult } from './compose';

export type ReelProps = {
  s: RoomSnapshot;
  fan: Fan;
  take: Take;
  localUrl?: string;
  takes: Take[];
  plays: readonly GameEvent[];
  lockUi: LockUi;
  urlOf: (t: Take) => string;
  onClose: () => void;
};

export function ReelSheet(p: ReelProps) {
  const { s, take } = p;
  const playMap = useMemo(() => new Map(p.plays.map((e) => [e.seq, e])), [p.plays]);
  const sel = useMemo(
    () => selectReelSegments(s.probability, take.marks, { durationMs: take.durationMs, plays: p.plays }),
    [s.probability, take, p.plays],
  );
  const friends = useMemo(
    () =>
      p.takes
        .filter((t) => t.fanId !== p.fan.fanId)
        .map((t) => ({ t, pairs: pairSegments(sel.segments, t.marks, t.durationMs) }))
        .filter((x) => x.pairs.length > 0),
    [p.takes, p.fan.fanId, sel.segments],
  );
  const [friendId, setFriendId] = useState<string>();
  const friend = friends.find((f) => f.t.takeId === friendId);

  const [result, setResult] = useState<ReelResult>();
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string>();
  const [note, setNote] = useState<string>();
  const [muted, setMuted] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const run = useRef(0);
  // New live plays do not restart a cut whose recorded segments are unchanged.
  const selectionKey = JSON.stringify(sel.segments);
  useEffect(() => () => { if (result) URL.revokeObjectURL(result.url); }, [result]);

  const myLock = p.lockUi.phase === 'locked' ? p.lockUi.lock : undefined;
  const mine = s.resolution?.results.find((r) => r.fanId === p.fan.fanId);
  const line =
    myLock && s.call
      ? callLine({
          name: firstName(p.fan.displayName),
          label: choiceLabel(s.call, myLock.choice),
          ...(mine ? { correct: mine.correct } : {}),
          pAtLock: myLock.pChoiceAtLock,
        })
      : undefined;

  useEffect(() => {
    if (!sel.segments.length) return;
    const id = ++run.current;
    const controller = new AbortController();
    setResult(undefined);
    setError(undefined);
    setProgress(0);
    const segments = friend ? friend.pairs : sel.segments;
    composeReel({
      mine: { url: p.localUrl ?? p.urlOf(take), durationMs: take.durationMs, name: firstName(p.fan.displayName) },
      ...(friend ? { friend: { url: p.urlOf(friend.t), durationMs: friend.t.durationMs, name: firstName(friend.t.displayName) } } : {}),
      segments,
      plays: playMap,
      meta: s.meta,
      ...(line ? { callLine: line } : {}),
      ...(s.resolution ? { final: { away: s.resolution.finalAway, home: s.resolution.finalHome } } : {}),
      onProgress: (x) => id === run.current && setProgress(x),
      signal: controller.signal,
    })
      .then((r) => {
        if (id === run.current) setResult(r);
        else URL.revokeObjectURL(r.url);
      })
      .catch(() => id === run.current && setError('Your original take is still saved. Try making the reel again.'));
    return () => { run.current++; controller.abort(); };
  }, [friendId, selectionKey, take.takeId, attempt]); // re-cut only for changed segments/layout or an explicit retry

  const share = async () => {
    if (!result) return;
    const ext = result.mimeType.includes('mp4') ? 'mp4' : 'webm';
    const r = await shareReel(result.blob, `clutch-reel-${s.meta.away.tricode}-${s.meta.home.tricode}.${ext}`, line ?? 'My Clutch Reel');
    setNote(r === 'shared' ? 'Shared' : r === 'downloaded' ? 'Saved to your downloads' : undefined);
  };

  return (
    <div className="sheet-scrim">
      <section className="sheet reel" role="dialog" aria-modal="true" aria-label="Your Clutch Reel" data-testid="reel-sheet">
        <span className="sheet__grab" aria-hidden="true" />
        <header className="reel__head">
          <span className="wordmark wordmark--sm">CLUTCH</span>
          <span className="reel__tag">REEL</span>
          <button className="reel__x" onClick={p.onClose} aria-label="Close reel">
            ✕
          </button>
        </header>

        {sel.segments.length === 0 ? (
          <p className="reel__empty" data-testid="reel-empty">
            Nothing swung the game while you were recording. Keep the cam on through the next big play.
          </p>
        ) : (
          <>
            <div className={`reel__stage${result ? ' is-ready' : ''}`}>
              {result ? (
                <>
                  <video
                    className="reel__video"
                    src={result.url}
                    playsInline
                    autoPlay
                    loop
                    muted={muted}
                    onClick={(e) => {
                      const v = e.currentTarget;
                      if (v.paused) void v.play();
                      else v.pause();
                    }}
                    data-testid="reel-video"
                    data-bytes={result.blob.size}
                    data-mime={result.mimeType}
                  />
                  <button className="reel__sound" onClick={() => setMuted((m) => !m)} aria-pressed={!muted}>
                    {muted ? '🔇 Tap for sound' : '🔊 Sound on'}
                  </button>
                </>
              ) : error ? (
                <div className="reel__cutting" role="alert">
                  <span className="reel__cutting-label">The reel couldn’t finish</span>
                </div>
              ) : (
                <div className="reel__cutting" data-testid="reel-cutting">
                  <span className="reel__cutting-label">Cutting your reel</span>
                  <span className="reel__bar">
                    <i style={{ transform: `scaleX(${progress})` } as CSSProperties} />
                  </span>
                </div>
              )}
            </div>

            <p className="reel__picked">
              {sel.basis === 'model' ? 'The plays that swung the game while you were rolling' : 'The biggest plays while you were rolling'}
            </p>
            <ol className="reel__plays" data-testid="reel-plays">
              {sel.segments.flatMap((g) => g.beats).map((g) => {
                const e = playMap.get(g.seq);
                return (
                  <li key={g.seq} className="reel__play" style={{ '--c': e?.side ? `var(--${e.side})` : 'var(--volt)' } as CSSProperties}>
                    <Headshot personId={e?.personId} source={s.meta.source} name={e?.playerName} size={36} ring={e?.side ? `var(--${e.side})` : undefined} />
                    <span className="reel__play-txt">{e ? reelCaption(e) : `Play ${g.seq}`}</span>
                    {sel.basis === 'model' && <span className="reel__swing">{Math.round(g.swing * 100)}% swing</span>}
                  </li>
                );
              })}
            </ol>

            {friends.length > 0 && (
              <div className="reel__modes" role="group" aria-label="Reel layout">
                <button className={`reel__mode${!friend ? ' is-on' : ''}`} onClick={() => setFriendId(undefined)} aria-pressed={!friend}>
                  Just me
                </button>
                {friends.map((f) => (
                  <button
                    key={f.t.takeId}
                    className={`reel__mode${friend?.t.takeId === f.t.takeId ? ' is-on' : ''}`}
                    onClick={() => setFriendId(f.t.takeId)}
                    aria-pressed={friend?.t.takeId === f.t.takeId}
                    data-testid="reel-split"
                  >
                    Side by side · {firstName(f.t.displayName)}
                  </button>
                ))}
              </div>
            )}

            {error && <p className="alert">{error}</p>}
            {error ? <button className="btn btn--primary btn--block" onClick={() => setAttempt((n) => n + 1)} data-testid="reel-retry">Try again</button> : (
              <button className="btn btn--primary btn--block" disabled={!result} onClick={share} data-testid="reel-share">
                {result ? 'Share reel' : 'Cutting…'}
              </button>
            )}
            {note && <p className="reel__note">{note}</p>}
          </>
        )}
      </section>
    </div>
  );
}
